import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { HelpCircle, Paperclip, Download, Package, Clock, CheckCircle } from "lucide-react";
import { getRastreioPublico, publicComprovanteDownloadUrl, formatDate, formatDateTime } from "@/lib/ocorenApi";
import { getCategoria } from "@/lib/categorias";

export const RastreioPublico = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  // Se for CT-e, o usuário seleciona uma das NFs para ver a timeline dela
  const [selectedNf, setSelectedNf] = useState(null);
  const [selectedNfTimeline, setSelectedNfTimeline] = useState([]);
  const [selectedNfLoading, setSelectedNfLoading] = useState(false);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    setErro(null);
    getRastreioPublico(token)
      .then((res) => {
        setData(res);
        if (res.tipo === "cte" && res.nfs && res.nfs.length > 0) {
          // Por padrão seleciona a primeira nota
          handleSelectNf(res.nfs[0].nfe_numero, res.nfs[0].cnpj_cliente);
        }
      })
      .catch((err) => {
        setErro(err?.response?.data?.detail || "Link de rastreamento inválido ou expirado.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [token]);

  const handleSelectNf = async (nfNumero, cnpjCliente) => {
    setSelectedNf(nfNumero);
    setSelectedNfLoading(true);
    try {
      // Para obter a timeline pública da nota específica, podemos ler direto das movimentações do CT-e filtrando por nota
      if (data && data.tipo === "cte") {
        // Busca a timeline associada filtrando
        // Como o backend já resolveu o CT-e com as movimentações, podemos fazer um request ou filtrar local
        // Mas o rastreio do CT-e no backend não retorna todas as movimentações detalhadas de todas as NFs de uma vez por performance,
        // então nós consultamos a timeline pública usando o token + número da nota
        // Para simplificar, a API pública retorna todas as NFs e suas movimentações principais
      }
    } catch {
      // ignore
    } finally {
      setSelectedNfLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex flex-col items-center justify-center py-12 px-4">
        <div className="text-sm text-[#52525B] font-medium animate-pulse">
          Carregando informações de entrega...
        </div>
      </div>
    );
  }

  if (erro || !data) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex flex-col items-center justify-center py-12 px-4">
        <div className="bg-white p-8 rounded-lg border border-red-100 shadow-sm max-w-md w-full text-center space-y-4">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-50 text-red-500">
            <HelpCircle size={24} />
          </div>
          <h2 className="text-lg font-bold text-[#09090B]">Rastreamento Indisponível</h2>
          <p className="text-xs text-[#52525B] leading-relaxed">{erro}</p>
        </div>
      </div>
    );
  }

  const cat = getCategoria(data.categoria);
  const IconStatus = cat ? cat.icon : HelpCircle;

  return (
    <div className="min-h-screen bg-[#F3F4F6] flex flex-col font-sans">
      <header className="bg-white border-b border-[#E5E7EB] py-3 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-center">
          <img src="/logo_docss.png" alt="DocSS Logística" className="h-10 w-auto object-contain" />
        </div>
      </header>

      {/* Conteúdo Principal */}
      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-8 space-y-6">
        
        {/* Card Principal */}
        <div className="bg-white rounded-lg border border-[#E5E7EB] shadow-sm overflow-hidden">
          
          {/* Badge de Status no Topo */}
          <div className="px-6 py-4 bg-[#F9FAFB] border-b border-[#E5E7EB] flex flex-wrap items-center justify-between gap-3">
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full border"
              style={{
                backgroundColor: cat?.bg ? undefined : "#F3F4F6",
                color: cat?.color || "#52525B",
                borderColor: cat?.border ? undefined : "#E5E7EB"
              }}
              className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full border ${cat?.bg || "bg-zinc-100"} ${cat?.text || "text-zinc-700"} ${cat?.border || "border-zinc-200"}`}
            >
              <IconStatus size={13} />
              {(cat?.label || "Status").toUpperCase()}
            </span>
            <div className="text-[11px] text-[#6B7280] font-medium">
              Atualizado em tempo real
            </div>
          </div>

          {/* Dados do Rastreio */}
          <div className="p-6">
            <h1 className="text-lg font-bold text-[#111827] mb-6">
              Acompanhamento de Entrega
            </h1>

            <div className="grid grid-cols-2 gap-y-5 gap-x-6 text-xs">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-semibold mb-1">
                  {data.tipo === "nota" ? "Nota Fiscal" : "CT-e"}
                </div>
                <div className="font-mono-data font-bold text-[#111827]">
                  {data.tipo === "nota" ? data.numero_nota : data.cte_numero}
                </div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-semibold mb-1">
                  Data de Emissão
                </div>
                <div className="font-mono-data font-medium text-[#374151]">
                  {data.data_emissao ? formatDate(data.data_emissao) : "—"}
                </div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-semibold mb-1">
                  Destinatário
                </div>
                <div className="font-semibold text-[#111827] uppercase">
                  {data.destinatario || "—"}
                </div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-semibold mb-1">
                  Transportadora / Emitente
                </div>
                <div className="font-semibold text-[#111827] uppercase">
                  {data.emitente || "—"}
                </div>
              </div>

              {data.tipo === "nota" && data.dprev_entrega && (
                <div className="col-span-2 border-t border-[#F3F4F6] pt-4">
                  <div className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-semibold mb-1">
                    Previsão de Entrega
                  </div>
                  <div className="font-bold text-emerald-600 font-mono-data">
                    {formatDate(data.dprev_entrega)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Listagem de Notas (se for CT-e) */}
        {data.tipo === "cte" && data.nfs && data.nfs.length > 0 && (
          <div className="bg-white rounded-lg border border-[#E5E7EB] shadow-sm p-6 space-y-3">
            <h3 className="text-xs uppercase tracking-wider text-[#9CA3AF] font-bold">
              Notas Fiscais vinculadas a este CT-e ({data.nfs.length})
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {data.nfs.map((nf) => {
                const nfCat = getCategoria(nf.categoria);
                const NfIcon = nfCat ? nfCat.icon : HelpCircle;
                return (
                  <button
                    key={nf.nfe_numero}
                    onClick={() => handleSelectNf(nf.nfe_numero, nf.cnpj_cliente)}
                    className={`flex items-center justify-between p-3 rounded-md border text-left transition-all ${
                      selectedNf === nf.nfe_numero
                        ? "border-[#111827] bg-[#F9FAFB] ring-1 ring-[#111827]"
                        : "border-[#E5E7EB] bg-white hover:bg-zinc-50"
                    }`}
                  >
                    <div>
                      <div className="text-xs font-bold text-[#111827]">
                        NF {nf.nfe_numero}
                      </div>
                      <div className="text-[10px] text-[#6B7280] mt-0.5">
                        {nf.total_eventos} eventos
                      </div>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full border ${
                        nfCat ? `${nfCat.bg} ${nfCat.text} ${nfCat.border}` : "bg-zinc-100 text-zinc-500 border-zinc-200"
                      }`}
                    >
                      <NfIcon size={10} />
                      {nfCat ? nfCat.label : "Sem rastreio"}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Timeline */}
        <div className="bg-white rounded-lg border border-[#E5E7EB] shadow-sm p-6">
          <h2 className="text-sm font-bold text-[#111827] mb-6 flex items-center gap-2">
            <Package size={16} className="text-[#6B7280]" />
            Histórico da Entrega
            {selectedNf && <span className="text-xs font-normal text-[#6B7280]">(NF {selectedNf})</span>}
          </h2>

          {/* Timeline Vertical */}
          <div className="relative border-l-2 border-[#E5E7EB] ml-3 pl-6 space-y-6">
            {data.tipo === "nota" ? (
              // Timeline para Nota
              data.timeline && data.timeline.length > 0 ? (
                data.timeline.map((item, idx) => {
                  const itemCat = getCategoria(item.categoria);
                  const isLatest = idx === 0;
                  return (
                    <div key={idx} className="relative">
                      {/* Indicador de Timeline */}
                      <span
                        className={`absolute -left-[31px] top-1.5 flex h-4 w-4 items-center justify-center rounded-full border-2 bg-white transition-all ${
                          isLatest
                            ? "border-indigo-600 scale-110 ring-4 ring-indigo-50"
                            : "border-[#D1D5DB]"
                        }`}
                      >
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${
                            isLatest ? "bg-indigo-600 animate-pulse" : "bg-transparent"
                          }`}
                        />
                      </span>

                      <div>
                        <div className="text-[10px] text-[#9CA3AF] font-mono-data font-semibold">
                          {formatDateTime(item.data_ocorrencia)}
                        </div>
                        <h4 className="text-xs font-bold text-[#111827] mt-0.5">
                          {item.descricao_ocorrencia}
                        </h4>
                        {item.codigo_ocorrencia && (
                          <span className="inline-block mt-1 text-[9px] font-semibold text-[#6B7280] bg-[#F3F4F6] px-1.5 py-0.5 rounded-sm font-mono-data">
                            Cód: {item.codigo_ocorrencia}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-xs text-[#9CA3AF] py-4 text-center">
                  Aguardando atualização das movimentações de entrega.
                </div>
              )
            ) : (
              // Timeline para CT-e (busca a timeline da NF selecionada do CT-e)
              // No CT-e, podemos consolidar as movimentações correspondentes à nota selecionada
              data.timeline && data.timeline.length > 0 ? (
                data.timeline
                  .filter((item) => !selectedNf || item.numero_nota === selectedNf)
                  .map((item, idx) => {
                    const isLatest = idx === 0;
                    return (
                      <div key={idx} className="relative">
                        <span
                          className={`absolute -left-[31px] top-1.5 flex h-4 w-4 items-center justify-center rounded-full border-2 bg-white transition-all ${
                            isLatest
                              ? "border-indigo-600 scale-110 ring-4 ring-indigo-50"
                              : "border-[#D1D5DB]"
                          }`}
                        >
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${
                              isLatest ? "bg-indigo-600" : "bg-transparent"
                            }`}
                          />
                        </span>

                        <div>
                          <div className="text-[10px] text-[#9CA3AF] font-mono-data font-semibold">
                            {formatDateTime(item.data_ocorrencia)}
                          </div>
                          <h4 className="text-xs font-bold text-[#111827] mt-0.5">
                            {item.descricao_ocorrencia}
                          </h4>
                          {item.codigo_ocorrencia && (
                            <span className="inline-block mt-1 text-[9px] font-semibold text-[#6B7280] bg-[#F3F4F6] px-1.5 py-0.5 rounded-sm font-mono-data">
                              Cód: {item.codigo_ocorrencia}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
              ) : (
                <div className="text-xs text-[#9CA3AF] py-4 text-center">
                  Selecione uma nota fiscal acima para visualizar seu histórico.
                </div>
              )
            )}
          </div>
        </div>

        {/* Comprovantes e Anexos (Somente Leitura) */}
        {data.comprovantes && data.comprovantes.length > 0 && (
          <div className="bg-white rounded-lg border border-[#E5E7EB] shadow-sm p-6 space-y-4">
            <h3 className="text-sm font-bold text-[#111827] flex items-center gap-2">
              <Paperclip size={15} className="text-[#6B7280]" />
              Documentos Anexados
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {data.comprovantes.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center justify-between p-3 rounded-md border border-[#E5E7EB] bg-[#F9FAFB] text-xs"
                >
                  <div className="min-w-0 flex-1 pr-2">
                    <div
                      title={c.original_filename}
                      className="font-medium text-[#111827] truncate"
                    >
                      {c.original_filename}
                    </div>
                    <div className="text-[9px] text-[#9CA3AF] mt-0.5">
                      {formatDateTime(c.created_at)}
                    </div>
                  </div>
                  <a
                    href={publicComprovanteDownloadUrl(c.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="p-1.5 rounded-md text-zinc-500 hover:text-white bg-white hover:bg-zinc-800 border border-[#E5E7EB] shadow-sm transition-all"
                    title="Baixar arquivo"
                  >
                    <Download size={13} />
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

      </main>

      <footer className="bg-white border-t border-[#E5E7EB] py-6 text-center space-y-2">
        <div className="text-[11px] text-[#52525B] font-medium">
          Este é um serviço seguro provido pela <span className="font-bold text-[#09090B]">DocSS Logística</span>
        </div>
        <div className="text-[10px]">
          <a
            href="https://docssplus.com.br"
            target="_blank"
            rel="noreferrer"
            className="text-indigo-600 hover:text-indigo-800 font-semibold underline hover:no-underline transition-colors"
          >
            Descubra como o DocSS pode automatizar sua empresa →
          </a>
        </div>
      </footer>
    </div>
  );
};

export default RastreioPublico;
